import { Component, inject, input, output, signal, computed, viewChild, TemplateRef } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppState } from 'src/app/app.state';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { IdTagTypeEnum } from 'src/app/shared/enums/id-tag-type.enum';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { IdTag } from '../id-tag-model';
import { EnvironmentLoaderService } from '../../../shared/services/environment-loader.service';
import { IdTagService } from '../../../shared/services/id-tag.service';
import { IdTagListStateService } from '../id-tags-overview/id-tag-list.state.service';
import {
  CardComponent,
  ExpandListComponent,
  ExpandListSort,
  ExpandListConfiguration,
  SnackbarService,
  ScreenSizeEnum,
} from 'ok-angular-shared/components';
import { IdTagCreateOrEditModalComponent } from '../id-tag-create-or-edit-modal/id-tag-create-or-edit-modal.component';
import { IdTagProcessModalComponent } from '../id-tag-process-modal/id-tag-process-modal.component';
import { IdTagDownloadUsageReportModalComponent } from '../id-tag-download-usage-report-modal/id-tag-download-usage-report-modal.component';
import { IdTagSwitchDepartmentModalComponent } from '../id-tag-switch-department-modal/id-tag-switch-department-modal.component';
import { IdTagConvertToFleetIdTagModalComponent } from '../id-tag-convert-to-fleet-id-tag-modal/id-tag-convert-to-fleet-id-tag-modal.component';
import { IdTagAccessGroupsModalComponent } from '../id-tag-access-groups-modal/id-tag-access-groups-modal.component';
import { IdTagToggleBlockedModalComponent } from '../id-tag-toggle-blocked-modal/id-tag-toggle-blocked-modal.component';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { AuthService } from '../../../core/auth/auth.service';
import { Permission } from '../../../shared/enums/permission.enum';
import { IdTagDetailsComponent } from '../id-tag-details/id-tag-details.component';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { isAnyEnumStatus, isEnumStatus } from 'src/app/shared/utilities/enum-utility';

@Component({
  selector: 'adm-id-tags-list',
  templateUrl: './id-tags-list.component.html',
  styleUrls: ['./id-tags-list.component.scss'],
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    CardComponent,
    ExpandListComponent,
    IdTagDetailsComponent,
    MatPaginator,
  ],
})
export class IdTagsListComponent {
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private idTagService = inject(IdTagService);
  private snackbarService = inject(SnackbarService);
  private authService = inject(AuthService);
  appState = inject(AppState);
  state = inject(IdTagListStateService);

  printedId = input<number>();
  refreshAfterUpdateEvent = output<IdTag>();

  public idTagDetailTemplate = viewChild<TemplateRef<IdTagDetailsComponent>>('idTagDetail');
  public printedIdTemplate = viewChild<TemplateRef<any>>('printedIdTemplate');
  public statusTemplate = viewChild<TemplateRef<any>>('statusTemplate');
  public organizationNameTemplate = viewChild<TemplateRef<any>>('organizationNameTemplate');
  public departmentNameTemplate = viewChild<TemplateRef<any>>('departmentNameTemplate');
  public updatedTemplate = viewChild<TemplateRef<any>>('updatedTemplate');

  public roles = this.environmentLoaderService.virtualRoles;
  public idTagStatusEnum = IdTagStatusEnum;
  public idTagTypeEnum = IdTagTypeEnum;
  public idTagUserTypeEnum = IdTagUserType;
  private readonly canWrite = this.authService.hasRole(this.roles[Permission.IdTagWrite] ?? '');
  private readonly canEditIdTag = this.authService.hasRole(this.roles[Permission.IdTagEdit] ?? '');
  private readonly canConvertToFleetIdTag = this.authService.hasRole(this.roles[Permission.ConvertToFleetIdTag] ?? '');
  private readonly canToggleAccess = this.authService.hasRole(this.roles[Permission.IdTagToggleAccess] ?? '');
  private readonly canDownloadReport = this.authService.hasRole(this.roles[Permission.CustomerReportDownload] ?? '');

  public currentSort = signal<ExpandListSort<IdTag>>({ column: 'printedId', direction: 'asc' });

  public config = computed<ExpandListConfiguration<IdTag>>(() => ({
    classFn: item => (item.highlight?.getValue() ? 'highlight' : ''),
    columns: [
      { field: 'status', header: '', width: '100px', sortField: 'status', displayTemplate: this.statusTemplate(), disableSort: true },
      { field: 'printedId', header: 'Brik ID', width: '150px', sortField: 'printedId' },
      { field: 'description', header: 'Beskrivelse', width: '1fr', sortField: 'description' },
      {
        field: 'organizationName',
        header: 'Organisation',
        width: '1fr',
        sortField: 'organizationName',
        displayTemplate: this.organizationNameTemplate(),
      },
      {
        field: 'departmentName',
        header: 'Afdeling',
        width: '1fr',
        sortField: 'departmentName',
        displayTemplate: this.departmentNameTemplate(),
      },
      { field: 'updatedBy', header: 'Ændret af', width: '1fr', sortField: 'updatedBy', visibleFrom: ScreenSizeEnum.md },
      {
        field: 'updated',
        header: 'Senest ændret',
        width: '1fr',
        sortField: 'updated',
        visibleFrom: ScreenSizeEnum.md,
        displayTemplate: this.updatedTemplate(),
      },
    ],
    hideActionsFn: () => {
      return !this.canWrite && !this.canToggleAccess && !this.canDownloadReport && !this.canConvertToFleetIdTag;
    },
    actions: [
      {
        icon: { name: 'edit-s' },
        label: 'Rediger brik',
        action: (item?: IdTag) => item && this.openEditIdTagModal(item),
        hiddenFn: (item?: IdTag) =>
          !item || !this.canEditIdTag || isEnumStatus(item.status, IdTagStatusEnum.Inactive, this.idTagStatusEnum),
      },
      {
        icon: { name: 'download-s' },
        label: 'Hent forbrugsrapport',
        action: (item?: IdTag) => item && this.openDownloadReportModal(item),
        hiddenFn: (item?: IdTag) =>
          !item || !this.canDownloadReport || isEnumStatus(item.status, IdTagStatusEnum.Pending, this.idTagStatusEnum),
      },
      {
        svgIcon: 'move_down',
        label: 'Skift afdeling',
        action: (item?: IdTag) => item && this.openSwitchDepartmentModal(item),
        hiddenFn: (item?: IdTag) =>
          !item ||
          !this.canWrite ||
          item.idTagType.toString() !== this.idTagTypeEnum[this.idTagTypeEnum.FleetIdTag] ||
          item.departmentId === 0 ||
          isAnyEnumStatus(
            item.status,
            [IdTagStatusEnum.Inactive, IdTagStatusEnum.Blocked, IdTagStatusEnum.NoIdTagGroup, IdTagStatusEnum.Pending],
            this.idTagStatusEnum
          ),
      },
      {
        icon: { name: 'token-s' },
        label: 'Behandl brik',
        action: (item?: IdTag) => item && this.openEditIdTagModal(item, true),
        hiddenFn: (item?: IdTag) =>
          !item || !this.canWrite || item.idToken !== null || isEnumStatus(item.status, IdTagStatusEnum.Blocked, this.idTagStatusEnum),
      },
      {
        svgIcon: 'block',
        label: 'Fjern adgang',
        action: (item?: IdTag) => item && this.openToggleBlockedModal(item),
        hiddenFn: (item?: IdTag) =>
          !item ||
          !this.canToggleAccess ||
          isAnyEnumStatus(
            item.status,
            [IdTagStatusEnum.Pending, IdTagStatusEnum.Inactive, IdTagStatusEnum.Blocked, IdTagStatusEnum.NoIdTagGroup],
            this.idTagStatusEnum
          ),
      },
      {
        svgIcon: 'check_circle',
        label: 'Giv adgang',
        action: (item?: IdTag) => item && this.openToggleBlockedModal(item),
        hiddenFn: (item?: IdTag) =>
          !item ||
          !this.canToggleAccess ||
          isAnyEnumStatus(
            item.status,
            [IdTagStatusEnum.Pending, IdTagStatusEnum.Inactive, IdTagStatusEnum.Blocked],
            this.idTagStatusEnum
          ) ||
          !isEnumStatus(item.status, IdTagStatusEnum.NoIdTagGroup, this.idTagStatusEnum),
      },
      {
        icon: { name: 'trash-s' },
        label: 'Slet bestilling',
        action: (item?: IdTag) => item && this.openDeleteIdTagModal(item),
        hiddenFn: (item?: IdTag) => !item || !this.canWrite || !isEnumStatus(item.status, IdTagStatusEnum.Pending, this.idTagStatusEnum),
      },
      {
        svgIcon: 'sell_outlined',
        label: 'Konverter til flådebilsbrik',
        action: (item?: IdTag) => item && this.openConvertToFleetIdTagModal(item),
        hiddenFn: (item?: IdTag) =>
          !item ||
          item.idTagType.toString() !== this.idTagTypeEnum[this.idTagTypeEnum.ChargingUserIdTag] ||
          !this.canConvertToFleetIdTag ||
          !isEnumStatus(item.status, IdTagStatusEnum.Active, this.idTagStatusEnum) ||
          item.idTagUserType.toString() === this.idTagUserTypeEnum[this.idTagUserTypeEnum.NoCompanyCar],
      },
      {
        icon: { name: 'users-s' },
        label: 'Rediger adgangsgrupper',
        action: (item?: IdTag) => item && this.accessGroups(item),
        hiddenFn: (item?: IdTag) =>
          !item || item.idTagType.toString() !== this.idTagTypeEnum[this.idTagTypeEnum.FleetIdTag] || !this.canWrite,
      },
    ],
  }));

  public pageSwitch(event: PageEvent) {
    this.state.updatePage(event);
  }

  public sortData(sort: ExpandListSort<IdTag>) {
    this.currentSort.set(sort);

    // Convert ExpandListSort to Material Sort format and update state
    const matSort: Sort = {
      active: sort.column as string,
      direction: sort.direction,
    };

    this.state.updateSort(matSort);
  }

  // Action methods from id-tag-row component
  public openEditIdTagModal(idTag: IdTag, isSetRfIdOnly = false) {
    if (isSetRfIdOnly) {
      this.processIdTag(idTag);
    } else {
      const dialogRef = this.dialog.open(IdTagCreateOrEditModalComponent, {
        data: {
          idTagId: idTag.id,
        },
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (!result?.idTag) {
          return;
        }
        this.state.refreshIdTag(result.idTag);
        if (result.action === 'process' && result.idTag) {
          this.processIdTag(result.idTag);
          return;
        }
      });
    }
  }

  processIdTag(idTag: IdTag) {
    const dialogRef = this.dialog.open(IdTagProcessModalComponent, {
      data: {
        idTagId: idTag.id,
      },
    });

    dialogRef.afterClosed().subscribe((idTag: IdTag) => {
      if (idTag) {
        this.state.refreshIdTag(idTag);
      }
    });
  }

  public openDownloadReportModal(idTag: IdTag) {
    const dialogRef = this.dialog.open(IdTagDownloadUsageReportModalComponent, {
      data: {
        idTag: idTag,
        organizationId: idTag.organizationId,
      },
    });
  }

  public openSwitchDepartmentModal(idTag: IdTag) {
    const dialogRef = this.dialog.open(IdTagSwitchDepartmentModalComponent, {
      data: {
        idTagId: idTag.id,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      const savedId = dialogRef.componentInstance.savedId();
      if (savedId === null) return;

      this.state.refreshIdTag({ ...idTag, id: savedId }, idTag.id);
    });
  }

  public openConvertToFleetIdTagModal(idTag: IdTag) {
    const dialogRef = this.dialog.open(IdTagConvertToFleetIdTagModalComponent, {
      data: {
        idTagId: idTag.id,
      },
    });

    dialogRef.afterClosed().subscribe((idTag: IdTag) => {
      if (idTag) {
        this.state.refreshIdTag(idTag);
      }
    });
  }

  public accessGroups(idTag: IdTag) {
    const dialogRef = this.dialog.open(IdTagAccessGroupsModalComponent, {
      data: {
        idTag: idTag,
      },
    });
    dialogRef.afterClosed().subscribe(data => {
      if (data?.idTagUpdated) {
        this.state.refreshIdTag(data?.idTagUpdated);
      }
    });
  }

  public openToggleBlockedModal(idTag: IdTag) {
    console.warn('Opening toggle blocked modal for idTag:', idTag);
    const dialogRef = this.dialog.open(IdTagToggleBlockedModalComponent, {
      data: {
        idTagId: idTag.id,
      },
    });

    dialogRef.afterClosed().subscribe((idTag: IdTag) => {
      if (idTag) {
        this.state.refreshIdTag(idTag);
      }
    });
  }

  public openDeleteIdTagModal(idTag: IdTag) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Slet brik bestilling',
        text: `Er du sikker på at du vil slette denne brik bestilling?`,
        confirmText: 'Slet',
        denyText: 'Annuller',
        icon: { name: 'trash-s' },
      },
      disableClose: true,
    });
    confirmDialogRef.afterClosed().subscribe(async confirmed => {
      if (confirmed) {
        this.idTagService.deleteIdTag(idTag.id).subscribe({
          next: () => {
            idTag.deleted = true;
            this.state.refreshIdTag(idTag);
            this.snackbarService.success('Brik bestilling blev succesfuldt slettet.');
          },
          error: err => {
            console.error(err);
            this.snackbarService.error('Der skete en fejl. Brik bestilling blev ikke slettet.');
          },
        });
      }
    });
  }

  public goToChargingUser(chargingUserId: number) {
    this.router.navigateByUrl('/charging-users?chargingUserId=' + chargingUserId);
  }
}
